import { TimeoutService } from '@df/ng/timeout.service';
import { NgInject } from '@df/ng/ng-inject';
import keyBy from 'lodash-es/keyBy';
import min from 'lodash-es/min';
import sortBy from 'lodash-es/sortBy';
import { Product } from '@df/catalog/product/product';
import { ListingSortAttributeEngine } from './listing-sort-attribute-engine';
import { IListingSortEngine, IListingSortOption } from './listing-sort';
import { Listing } from '../listing';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2 } from '@df/catalog/product/product';

export interface ISectionItem {
    item: Product;
    position: number;
}

export interface ISortOptions {
    /**
     * If set then discounte items will be pushed to the bottom
     * @type {boolean}
     */
    banDiscounted?: boolean;
    /**
     * If true then banner position keep unchanged
     * @type {boolean}
     */
    bannerPositionsUnchanged?: boolean;
    /**
     * If set then we will allow taking positions from families if no direct hit
     * (with pio result)
     * @type {boolean}
     */
    useFamily?: boolean;
    /**
     * Hero products to be placed on top of the list
     */
    heroes?: number[];
}

export abstract class AbstractAsyncEngine extends ListingSortAttributeEngine<Product> implements IListingSortEngine<Product> {
    /**
     * NgZone service
     */
    @NgInject(TimeoutService)
    private timeoutService!: TimeoutService;

    /**
     * Fetch sorted ids
     */
    protected abstract fetch(listing: Listing<Product>, collection: Product[], cb: (err, ids: number[]) => void): void;

    /**
     * Check if engine can be applied
     */
    protected isOn(listing: Listing<Product>): boolean {
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;
        return sourceModel?.type === 'category';
    }

    /**
     * Retrieve sort options
     */
    protected getSortOptions(listing: Listing<Product>): ISortOptions {
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;
        return {
            banDiscounted: false,
            bannerPositionsUnchanged: true,
            useFamily: true,
            heroes: sourceModel?.data?.heroProducts
        };
    }

    /**
     * Public sort method
     */
    override sort(collection: Product[], option: IListingSortOption, listing: Listing<Product>, cb: (models: Product[]) => void): void {
        // sort by position
        const fallback = () => super.sort(collection, option, listing, cb);

        // do not apply if globally disabled
        if (!this.isOn(listing)) {
            return fallback();
        }

        const sortOptions = this.getSortOptions(listing);

        let finished = false;

        // create max waiting time timeout
        // after 0.5s apply native sort
        this.timeoutService.setTimeout(() => {
            if (!finished) {
                fallback();
            }
        }, 500);
        // do not wait longer then 2s
        this.timeoutService.setTimeout(() => (finished = true), 2000);
        this.fetch(listing, collection, (err, ids) => {
            if (!finished) {
                finished = true;
                cb(this.procesSort(collection, ids, sortOptions));
            }
        });
    }

    public procesSort(collection: Product[], desiredOrder: number[] | undefined, sortOptions: ISortOptions): Product[] {
        /////////////////  GENERATE MAP WITH SECTIONS /////////////////////
        const heroes: number[] = sortOptions.heroes || [];

        enum section {
            heroes, // hero product
            engine, // sorted by our engine product (to take top of the list)
            regular, // not-pio products (to follow pio)
            banned, // banned product moved to bottom
            banners // banners that should remain on their original positions
        }

        const map: { [section: number]: ISectionItem[] } = {};
        const finalItems: Product[] = [];

        if (!desiredOrder) {
            desiredOrder = [];
        }

        // lets group items to sections
        collection.forEach((item: Product, position: number) => {
            // item.data['isAsyncEngPos'] = false; // just for development (flag is set lower in this function)

            // if here then no matter what it is move to here products
            const heroPosition = heroes.indexOf(item.id);
            if (heroPosition !== -1) {
                return toSection(section.heroes, item, heroPosition);
            }

            // keep banners positions unchanged
            if (sortOptions.bannerPositionsUnchanged && item?.data?.['isBanner']) {
                return toSection(section.banners, item, position);
            }

            // shift discounted to banned
            if (sortOptions.banDiscounted && item.orgPrice) {
                return toSection(section.banned, item, position);
            }

            // if pio position provided then used i
            const desiredPosition = getDesiredPosition(item);
            if (desiredPosition !== -1 && desiredPosition !== undefined) {
                // item.data['isAsyncEngPos'] = true;
                return toSection(section.engine, item, desiredPosition);
            }

            // keep original position
            return toSection(section.regular, item, position);

            /**
             * Moves item to section
             * @author Maurycy Ornat maurycy@tomandco.co.uk
             * @param  {section} section
             * @param  {T} item
             * @param  {number}  position
             */
            function toSection(_section: section, _item: Product, _position: number): void {
                if (!map[_section]) {
                    map[_section] = [];
                }
                map[_section].push({ item: _item, position: _position });
            }

            /**
             * Take position directly from pio index
             * if not found then try takiung family products and pick best position
             * @author Maurycy Ornat maurycy@tomandco.co.uk
             * @param  {T} item
             */
            function getDesiredPosition(_item: Product): number {
                const _pioPosition = desiredOrder!.indexOf(_item.id);
                if (_pioPosition > -1 || !sortOptions.useFamily) {
                    return _pioPosition;
                }
                return min(
                    [(<IProductDataV2>_item.data).parent, ..._item.childrenIds]
                        .map(id => (id ? desiredOrder!.indexOf(id) : -1))
                        .filter(i => i > -1)
                ) as number;
            }
        });

        /////////////////  CREATE BANNERS INDEX /////////////////////

        // banners indexed by position
        const banners = keyBy(map[section.banners], 'position');

        /////////////////  SORT ITEMS BASING ON MAP /////////////////////

        // sort each map by position in it
        [section.heroes, section.engine, section.regular, section.banned].forEach(_section => {
            const sectionMap = map[_section];

            if (!sectionMap) {
                return;
            }

            if (banners[0]) {
                pushItem(banners[0]);
            }
            // sort map + push all items
            sortBy(sectionMap, 'position').forEach(pushItem);

            function pushItem(mapItem: ISectionItem) {
                const currentPosition = finalItems.push(mapItem.item);
                if (banners[currentPosition]) {
                    pushItem(banners[currentPosition]);
                }
            }
        });

        return finalItems;
    }
}
